import type { SpartanProductEngineItem } from '@/app/lcv/components/sections/spartan/products/engine/types';
import {
  SPARTAN_PRODUCT_HERO_BANNER_SPEC_LABEL_LIST,
  SPARTAN_PRODUCT_HERO_BANNER_SPEC_LABELS,
} from '@/app/lcv/components/sections/spartan/products/hero-banner/constants';
import type {
  SpartanProductHeroBannerContent,
  SpartanProductHeroBannerData,
  SpartanProductHeroBannerEngineSpec,
  SpartanProductHeroBannerEntranceAnimation,
  SpartanProductHeroBannerLayerInput,
  SpartanProductHeroBannerLogoInput,
  SpartanProductHeroBannerMainBg,
  SpartanProductHeroBannerModelLogoMedia,
  SpartanProductHeroBannerParallax,
  SpartanProductHeroBannerParallaxMedia,
  SpartanProductHeroBannerResponsiveSrc,
  SpartanProductHeroBannerSpec,
  SpartanProductHeroBannerSrcInput,
} from '@/app/lcv/components/sections/spartan/products/hero-banner/types';
import { formatPrice } from '@/app/utils/format';

export interface GetSpartanProductHeroBannerSpecsParams {
  engineSpecs?: readonly SpartanProductHeroBannerEngineSpec[];
  startPrice?: number;
}

export const getSpartanProductHeroBannerEntranceAnimation = (
  parallax?: SpartanProductHeroBannerParallax
): SpartanProductHeroBannerEntranceAnimation | undefined =>
  parallax?.entranceAnimation;

export const getSpartanProductHeroBannerSpecs = ({
  engineSpecs = [],
  startPrice,
}: GetSpartanProductHeroBannerSpecsParams): readonly SpartanProductHeroBannerSpec[] => {
  const torque = engineSpecs.flatMap((engine) =>
    engine.torque != null ? [String(engine.torque)] : []
  );
  const horsepower = engineSpecs.flatMap((engine) =>
    engine.horsepower != null ? [String(engine.horsepower)] : []
  );

  if (horsepower.length === 0 && torque.length === 0) return [];

  return [
    ...(horsepower.length > 0
      ? [
          {
            value: horsepower.join(' / '),
            label: SPARTAN_PRODUCT_HERO_BANNER_SPEC_LABEL_LIST[0],
          },
        ]
      : []),
    ...(torque.length > 0
      ? [
          {
            value: torque.join(' / '),
            label: SPARTAN_PRODUCT_HERO_BANNER_SPEC_LABEL_LIST[1],
          },
        ]
      : []),
    ...(startPrice != null
      ? [
          {
            value: formatPrice(startPrice, 'none'),
            label: SPARTAN_PRODUCT_HERO_BANNER_SPEC_LABELS.startPrice,
          },
        ]
      : []),
  ];
};

export const getSpartanProductHeroBannerEngineSpecs = (
  engines: readonly SpartanProductEngineItem[]
): SpartanProductHeroBannerEngineSpec[] => {
  const biturbo = engines.find((engine) => engine.engineKey === '2.2 Biturbo');
  const heroEngines = biturbo ? [biturbo] : engines;

  return heroEngines.map((engine) => ({
    label: engine.heroLabel,
    ...(engine.horsepower != null ? { horsepower: engine.horsepower } : {}),
    ...(engine.torque != null ? { torque: engine.torque } : {}),
  }));
};

const normalizeResponsiveSrc = (
  src: SpartanProductHeroBannerSrcInput
): SpartanProductHeroBannerResponsiveSrc => {
  if (typeof src === 'string') {
    return {
      mobile: { src },
      desktop: { src },
    };
  }

  return {
    mobile: { src: src.mobile },
    desktop: { src: src.desktop },
  };
};

const normalizeBackground = (
  background: NonNullable<
    SpartanProductHeroBannerContent['media']['background']
  >
): SpartanProductHeroBannerMainBg => ({
  ...normalizeResponsiveSrc(background.src),
  ...(background.scrollSpeed !== undefined
    ? { scrollSpeed: background.scrollSpeed }
    : {}),
});

const normalizeLayer = (
  layer: SpartanProductHeroBannerLayerInput
): SpartanProductHeroBannerParallaxMedia => {
  const src = normalizeResponsiveSrc(layer.src);

  return {
    mobile: { ...layer.layout.mobile, src: src.mobile.src },
    desktop: { ...layer.layout.desktop, src: src.desktop.src },
    ...(layer.scrollSpeed !== undefined
      ? { scrollSpeed: layer.scrollSpeed }
      : {}),
  };
};

const normalizeModelLogo = (
  logo: SpartanProductHeroBannerLogoInput
): SpartanProductHeroBannerModelLogoMedia => ({
  ...normalizeResponsiveSrc(logo.src),
  layout: logo.layout,
  ...(logo.className !== undefined ? { className: logo.className } : {}),
});

const normalizeParallax = (
  scene: Extract<
    SpartanProductHeroBannerContent['media'],
    { kind: 'layered' }
  >['scene']
): SpartanProductHeroBannerParallax => ({
  scene: scene.layout,
  ...(scene.scrollSpeed !== undefined
    ? { scrollSpeed: scene.scrollSpeed }
    : {}),
  car: normalizeLayer(scene.layers.car),
  ...(scene.layers.back ? { back: normalizeLayer(scene.layers.back) } : {}),
  ...(scene.layers.mid ? { mid: normalizeLayer(scene.layers.mid) } : {}),
  ...(scene.layers.front ? { front: normalizeLayer(scene.layers.front) } : {}),
  ...(scene.layers.blur ? { blur: normalizeLayer(scene.layers.blur) } : {}),
  ...(scene.entranceAnimation !== undefined
    ? { entranceAnimation: scene.entranceAnimation }
    : {}),
  ...(scene.fadeBackgroundOnEntrance !== undefined
    ? { fadeBackgroundOnEntrance: scene.fadeBackgroundOnEntrance }
    : {}),
  ...(scene.carEntranceDirection !== undefined
    ? { carEntranceDirection: scene.carEntranceDirection }
    : {}),
});

export const createSpartanProductHeroBannerData = (
  content: SpartanProductHeroBannerContent
): SpartanProductHeroBannerData => {
  const { media, modelOverview } = content;

  return {
    title: content.title,
    tagline: content.tagline,
    ...(content.productName !== undefined
      ? { productName: content.productName }
      : {}),
    ...(media.background
      ? { mainBg: normalizeBackground(media.background) }
      : {}),
    ...(media.lcpMedia ? { lcpMedia: media.lcpMedia } : {}),
    parallax: normalizeParallax(media.scene),
    ...(modelOverview
      ? { modelLogo: normalizeModelLogo(modelOverview.logo) }
      : {}),
    ...(modelOverview?.align
      ? { modelOverviewAlign: modelOverview.align }
      : {}),
    ...(modelOverview?.taglineBold !== undefined
      ? { modelOverviewTaglineBold: modelOverview.taglineBold }
      : {}),
    ...(modelOverview?.taglineAlign !== undefined
      ? { modelOverviewTaglineAlign: modelOverview.taglineAlign }
      : {}),
    ...(content.engineLogo ? { engineLogo: content.engineLogo } : {}),
    ...(content.specialLogo ? { specialLogo: content.specialLogo } : {}),
    ...(content.overlay ? { overlay: content.overlay } : {}),
  };
};
